'use client'

/**
 * E2E Dev-Overlay Neutralizer (Issue #10994)
 *
 * Next.js dev mode renders a `<nextjs-portal>` custom element (the HMR /
 * route-activity indicator, plus the runtime-error dialog when one fires).
 * Its host element covers a much larger hit-test area than the small
 * visible badge, so it intercepts Playwright pointer clicks on unrelated
 * page controls anywhere on the page — failing the actionability check
 * before the click ever reaches the real target.
 *
 * This only matters for automated (Playwright-driven) browsers, and only
 * in dev mode:
 * - `process.env.NODE_ENV !== 'development'` short-circuits at build time,
 *   so the effect body (everything past this check) is dead-code-eliminated
 *   from production bundles by the minifier — the component itself still
 *   mounts and renders `null`, negligible overhead. Moot either way: the
 *   `<nextjs-portal>` element does not exist in production (`next
 *   build`/`next start` never renders the dev overlay).
 * - `navigator.webdriver` is the standard automation signal every
 *   WebDriver-based tool (including Playwright's Chromium, headless or
 *   headed) sets to `true`. Real developers running `pnpm run dev` in a
 *   normal browser never trip this, so the indicator stays fully usable
 *   for everyday local development.
 *
 * Deliberately `pointer-events: none` on the portal HOST only — NOT
 * `display: none` — so this can never mask a real runtime error: the
 * error dialog (`[data-nextjs-dialog]`, asserted NOT-visible in
 * `tests/e2e/test-utils.ts` / `tests/e2e/pages/BasePage.ts`) is a
 * visibility check, not an interactivity check, and pointer-events does
 * not affect Playwright's `toBeVisible()`. A real error dialog still
 * renders and is still detected; only its ability to *intercept clicks
 * meant for other elements* is removed. Applied via a stylesheet rule
 * (tag selector) rather than querying/mutating the element directly, so
 * it takes effect even before Next.js's dev-overlay script upgrades the
 * custom element (no MutationObserver race, survives HMR remounts).
 *
 * Note: `pointer-events` is an inherited CSS property, so `none` on the
 * portal host also makes a real error dialog rendered inside it
 * non-interactive (unless Next's own internal styles re-assert
 * `pointer-events: auto` on specific descendants) — only its *visibility*
 * is guaranteed intact, not its clickability. No current spec needs to
 * click/dismiss the dev error dialog (only assert it is NOT visible), so
 * this doesn't bite today; worth knowing if a future spec needs to.
 *
 * Centralized here (root layout) rather than as a Playwright fixture:
 * roughly half of the e2e spec files import `test`/`expect` directly from
 * `@playwright/test` rather than the shared `tests/e2e/fixtures.ts`
 * export, so a fixture-based fix would silently miss them. A client-side
 * effect keyed on `navigator.webdriver` covers every route and every spec
 * uniformly, with no per-spec-file opt-in required, and needs no dev-server
 * restart or special env var — it evaluates fresh on every page load.
 */

import { useEffect } from 'react'

const STYLE_ELEMENT_ID = 'e2e-dev-overlay-neutralizer-style'

export function E2eDevOverlayNeutralizer(): null {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return
    if (typeof window === 'undefined') return
    if (!navigator.webdriver) return
    if (document.getElementById(STYLE_ELEMENT_ID)) return

    const style = document.createElement('style')
    style.id = STYLE_ELEMENT_ID
    style.textContent = 'nextjs-portal { pointer-events: none !important; }'
    document.head.appendChild(style)
  }, [])

  return null
}
